import React, { useState, useEffect, useCallback, CompositionEvent, SyntheticEvent } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import parse from 'date-fns/parse';
import de from 'date-fns/locale/de';
import { startOfDay } from '../../utils/Dates';
import './styles/react-datepicker.css';
import DateTimePickerContainer from './DateTimePickerContainer';

registerLocale('de', de);

const baseDate = startOfDay(new Date());

function isValidDate(str: string, format: string) {
    if (!str) return false;
    const date = parse(str, format, baseDate);
    if (!date) return false;

    return date.getTime() > 0;
}

function isValidTime(str: string, format: string) {
    if (!str) return false;
    const date = parse(str, format, baseDate);
    if (!date) return false;

    return date.getTime() > 0;
}

function allowTime(time: string): boolean {
    return /[0-9]|:/.test(time)
}

interface TimeInputProps {
    timeFormat: string
    date?: Date
    value?: string
    onChange?: (date: Date) => void
}

const TimeInput: React.FC<TimeInputProps> = ({ timeFormat, date, value, onChange }) => {
    const [time, setTime] = useState(value)
    const onBeforeInput = (event: SyntheticEvent) => {
        let eventc = event as CompositionEvent;
        if (!allowTime(eventc.data)) {
            event.preventDefault()
            return false
        }
        return true
    }
    const onChangeHandler = useCallback((times) => {
        if (isValidTime(times, timeFormat)) {
            onChange?.(times);
        }
        setTime(times)
    }, [onChange, timeFormat])
    return (
        <input
            value={time}
            maxLength={timeFormat.length}
            onBeforeInput={onBeforeInput}
            onChange={(e) => onChangeHandler(e.target.value)}
        />
    )
}

interface DateTimePickerProps {
    id: string
    placeholderText: string
    dateFormat: string
    timeFormat: string
    initialDate?: Date
    disabled?: boolean
    customInput?: React.ReactNode
    onChange: (date: Date) => void
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({ id, placeholderText, dateFormat, timeFormat, initialDate, disabled, customInput, onChange }) => {
    const [value, setValue] = useState<Date | undefined>(initialDate);
    const onChangeHandler = useCallback((date, event: React.SyntheticEvent<any> | undefined) => {
        // console.log('onChangeHandler', date);
        const fixedDate = startOfDay(date)
        setValue(fixedDate);
        onChange?.(fixedDate);
    }, [onChange]);
    useEffect(() => setValue(initialDate), [initialDate]);
    return (
        <ReactDatePicker
            id={id}
            placeholderText={placeholderText}
            showWeekNumbers
            locale="de"
            dateFormat={dateFormat}
            // openToDate={openToDate}
            customInput={customInput}
            // showTimeInput
            customTimeInput={<TimeInput timeFormat={timeFormat} />}
            selected={value}
            disabled={disabled}
            // calendarContainer={DateTimePickerContainer}
            calendarContainer={({ children }) => {
                return (
                    <DateTimePickerContainer title={Intl.DateTimeFormat().resolvedOptions().timeZone}>
                        {children}
                    </DateTimePickerContainer>
                )
            }}
            onChange={onChangeHandler}
            onChangeRaw={(event) => {
                const raw = event.target.value;
                // console.log('onChangeRaw', hidePopper, strictParsing, raw, event);
                if (!isValidDate(raw, dateFormat)) {
                    // console.log('onChangeRaw invalid value', raw);
                    setValue(undefined);
                    // if (onChange) onChange(undefined);
                }
            }}
        />
    );
};

export default DateTimePicker;
